import { Button, Stack, Text, Tooltip } from "@mantine/core";
import PopOver from "../View/PopOver";
import { IconChevronDown, IconTrash } from "@tabler/icons-react";
import { useLanguage } from "../../stores/LanguageStore";
import { useState } from "react";
import { notifications } from "@mantine/notifications";
import { transformFilterToServer } from "../../functions/utilFunctions";
import { modals } from "@mantine/modals";

const TableFilterDelete = ({
  isGlobal,
  handleDelete,
  id,
  apiRef,
  onFinish,
  selectedIds,
  filter,
  filterResultNumber,
  total,
  isPrm,
}) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(null);

  const main = (type, handleClose) => {
    setLoading(type);

    let data;

    if (type === "selection_inverse") {
      data = {
        id: id,
        ids: [],
        excluded_ids: selectedIds || [],
        filter: { mode: "or", values: [] },
        reverse_selection: false,
      };
    } else if (type === "all_but_filter") {
      data = {
        id: id,
        ids: [],
        excluded_ids: [],
        filter: transformFilterToServer(filter),
        reverse_selection: true,
      };
    } else if (type === "filter") {
      data = {
        id: id,
        ids: selectedIds || [],
        excluded_ids: [],
        filter: transformFilterToServer(filter),
        reverse_selection: false,
      };
    } else if (type === "all") {
      data = {
        id: id,
        ids: [],
        excluded_ids: [],
        filter: { mode: "or", values: [] },
        reverse_selection: false,
      };
    } else {
      data = {
        id: id,
        ids: selectedIds || [],
        excluded_ids: [],
        filter: { mode: "or", values: [] },
        reverse_selection: false,
      };
    }

    return handleDelete(data)
      .then((res) => {
        notifications.show({
          message: lang.contact_lists.delete_contacts.success,
          color: "tertiary.8",
        });

        if (apiRef && type !== "selection_inverse") {
          apiRef.current.setRowSelectionModel([]);

          selectedIds.forEach((id) => {
            document.querySelectorAll(`[data-id='${id}']`).forEach((el) => {
              el.style.animation = `forwards 1.5s deletedAnim ease`;
            });
          });

          setTimeout(() => {
            apiRef.current.updateRows(
              selectedIds.map((id) => ({ id, _action: "delete" })),
            );
          }, 750);
        }

        onFinish?.(type);
        handleClose();
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleModal = (type, handleClose) => {
    const getLabel = () => {
      if (type === "selection_inverse") {
        return {
          text: lang.contact_lists.delete_contacts.delete_all_but_selection,
          number: total - selectedIds.length,
        };
      }
      if (type === "filter") {
        return {
          text: lang.contact_lists.delete_contacts.delete_filter,
          number: filterResultNumber,
        };
      }
      if (type === "selection") {
        return {
          text: lang.contact_lists.delete_contacts.delete_selection,
          number: selectedIds.length,
        };
      }
      if (type === "all_but_filter") {
        return {
          text: lang.contact_lists.delete_contacts.delete_all_but_filter,
          number: total - filterResultNumber,
        };
      }
      return {
        text: lang.contact_lists.delete_contacts.delete_all,
        number: total,
      };
    };

    modals.openConfirmModal({
      title: <Text>{lang.contact_lists.delete_contacts.tooltip}</Text>,
      children: (
        <Text c="red">
          {lang.contact_lists.delete_contacts.delete_text}{" "}
          <span style={{ fontWeight: 600 }}>
            {getLabel().text} ({getLabel().number} contacts)
          </span>
        </Text>
      ),
      labels: {
        confirm: lang.global.delete,
        cancel: lang.global.cancel,
      },
      confirmProps: {
        color: "red",
        leftSection: <IconTrash size={18} />,
      },
      onConfirm: () => {
        main(type, handleClose);
      },
      onCancel: () => {},
      zIndex: 2000,
    });
  };

  return (
    <PopOver
      opener={(handleOpen, ref) => (
        <Button
          className="to-red"
          variant="light"
          color="red"
          ref={ref}
          // disabled={selectedIds?.length === 0 && filter?.items?.length === 0}
          leftSection={<IconTrash size={18} />}
          rightSection={<IconChevronDown size={16} />}
          justify="start"
          onClick={handleOpen}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
        >
          {lang.contact_lists.delete_contacts.tooltip}
        </Button>
      )}
      popoverChildren={(handleClose) => (
        <Stack gap={"xs"}>
          <Tooltip
            label={lang.contact_lists.delete_contacts.no_filter}
            disabled={filter?.items?.length > 0}
          >
            <Button
              loading={loading === "filter"}
              onClick={() => handleModal("filter", handleClose)}
              disabled={!filter || filter?.items?.length < 1}
              variant="subtle"
              size="sm"
              color="red"
            >
              {lang.contact_lists.delete_contacts.delete_filter}{" "}
              {filter?.items?.length > 0 ? `( ${filterResultNumber} )` : ""}
            </Button>
          </Tooltip>

          {!isPrm && (
            <Tooltip
              label={lang.contact_lists.delete_contacts.no_filter}
              disabled={filter?.items?.length > 0}
            >
              <Button
                loading={loading === "all_but_filter"}
                onClick={() => handleModal("all_but_filter", handleClose)}
                disabled={!filter || filter?.items?.length < 1}
                variant="subtle"
                size="sm"
                color="red"
              >
                {lang.contact_lists.delete_contacts.delete_all_but_filter}{" "}
                {filter?.items?.length > 0
                  ? `( ${total - filterResultNumber} )`
                  : ""}
              </Button>
            </Tooltip>
          )}
          <Tooltip
            label={lang.contact_lists.delete_contacts.no_selection}
            disabled={selectedIds?.length > 0}
          >
            <Button
              loading={loading === "selection"}
              onClick={() => handleModal("selection", handleClose)}
              disabled={!selectedIds || selectedIds?.length < 1}
              variant="subtle"
              size="sm"
              color="red"
            >
              {lang.contact_lists.delete_contacts.delete_selection}{" "}
              {selectedIds?.length > 0 ? `( ${selectedIds.length} )` : ""}
            </Button>
          </Tooltip>
          <Tooltip
            label={lang.contact_lists.delete_contacts.no_selection}
            disabled={selectedIds?.length > 0}
          >
            <Button
              loading={loading === "selection_inverse"}
              onClick={() => handleModal("selection_inverse", handleClose)}
              disabled={!selectedIds || selectedIds?.length < 1}
              variant="subtle"
              size="sm"
              color="red"
            >
              {lang.contact_lists.delete_contacts.delete_all_but_selection}{" "}
              {selectedIds?.length > 0
                ? `( ${total - selectedIds.length} )`
                : ""}
            </Button>
          </Tooltip>
          <Tooltip
            label={
              selectedIds?.length > 0
                ? lang.contact_lists.delete_contacts.delete_all_warning_disabled
                : lang.contact_lists.delete_contacts.delete_all
            }
          >
            <Button
              loading={loading === "all"}
              onClick={() => handleModal("all", handleClose)}
              disabled={total === 0 || selectedIds?.length > 0}
              variant="subtle"
              size="sm"
              color="black"
            >
              {lang.contact_lists.delete_contacts.delete_all}{" "}
              {selectedIds?.length > 0 ? `( ${total} )` : ""}
            </Button>
          </Tooltip>
        </Stack>
      )}
    />
  );
};

export default TableFilterDelete;
